import React, { useState } from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ddmmyyyy } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, History as HistoryIcon, Undo2, ScanEye } from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);

const SelectorEditor = ({ selector, onClose, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const [selectors, setSelectors] = useState(selector.selectors.join("\n"));
  const [description, setDescription] = useState(selector.description || "");
  const [isActive, setIsActive] = useState(selector.is_active);
  const { data: history } = useSWR(`/integrations/dpd/selectors/${selector.id}/history`, fetcher);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        selectors: selectors.split("\n").map((s) => s.trim()).filter(Boolean),
        description,
        is_active: isActive,
      };
      const { data } = await api.patch(`/integrations/dpd/selectors/${selector.id}`, payload);
      toast.success(`Saved v${data.version}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const rollback = async (versionId) => {
    if (!window.confirm("Roll back to this version?")) return;
    try {
      const { data } = await api.post(`/integrations/dpd/selectors/${selector.id}/rollback/${versionId}`);
      toast.success(`Rolled back to v${data.version}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Rollback failed");
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="selector-edit-dialog">
        <DialogHeader>
          <DialogTitle>{selector.selector_key}</DialogTitle>
          <DialogDescription>Version {selector.version} · Playwright will try each selector in order.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">CSS selectors (one per line)</Label>
            <Textarea value={selectors} onChange={(e) => setSelectors(e.target.value)}
                      rows={8} className="mt-1.5 font-mono text-xs"
                      data-testid="selectors-textarea" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1.5"
                   data-testid="selector-description" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-brand-border px-3 py-2">
            <div className="text-sm">Active</div>
            <Switch checked={isActive} onCheckedChange={setIsActive} data-testid="selector-active-toggle" />
          </div>

          {(history || []).length > 0 && (
            <div className="mt-3">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-brand-text-muted mb-2">
                <HistoryIcon className="w-3.5 h-3.5" /> Version history
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {history.map((h) => (
                  <div key={h.id} className="flex items-center justify-between text-xs p-2 border border-brand-border rounded-lg">
                    <div>
                      <span className="font-mono">v{h.version}</span>
                      <span className="text-brand-text-muted ml-2">{ddmmyyyy(h.created_at?.slice(0,10))}</span>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => rollback(h.id)} data-testid={`rollback-v${h.version}`}>
                      <Undo2 className="w-3 h-3 mr-1" /> Rollback
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-brand-red hover:bg-brand-red-hover"
                  data-testid="save-selector">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DpdSelectors = () => {
  const { data: selectors, mutate: reload } = useSWR("/integrations/dpd/selectors", fetcher);
  const [editing, setEditing] = useState(null);

  return (
    <div className="p-4 md:p-8 space-y-6">
      <Link to="/admin/integrations/dpd" className="inline-flex items-center gap-2 text-sm text-brand-text-muted hover:text-brand-text">
        <ArrowLeft className="w-4 h-4" /> Back to DPD Accounts
      </Link>
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Platform Owner · Advanced</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-brand-text mt-1">DPD Selector Registry</h1>
        <p className="text-sm text-brand-text-muted mt-1 max-w-2xl">
          Central place for the CSS selectors the Playwright worker uses. Update them here if DPD changes the portal — no redeploy needed.
        </p>
      </div>

      <div className="grid gap-3" data-testid="selectors-grid">
        {(selectors || []).map((s) => (
          <Card key={s.id} className="p-4 rounded-2xl border-brand-border"
                data-testid={`selector-${s.selector_key}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-sm font-bold">{s.selector_key}</div>
                <div className="text-xs text-brand-text-muted mt-1">{s.description}</div>
                <div className="text-[11px] mt-2 space-y-0.5">
                  {s.selectors.map((sel, i) => (
                    <div key={i} className="font-mono text-brand-text-muted truncate">→ {sel}</div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-brand-surface">v{s.version}</span>
                <Button size="sm" variant="outline" onClick={() => setEditing(s)}
                        data-testid={`edit-selector-${s.selector_key}`}>
                  <ScanEye className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {editing && (
        <SelectorEditor selector={editing} onClose={() => setEditing(null)} onSaved={reload} />
      )}
    </div>
  );
};

export default DpdSelectors;
